import cn from 'classnames';
import OpFavourite from 'components/Common/op-ui-wrappers/OpFavourite/OpFavourite';
import OpProductPreview from 'components/Common/op-ui-wrappers/OpProductPreview/OpProductPreview';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import Price from 'components/Common/Price/Price';
import PriceDiscountPercentage from 'components/Common/PriceDiscountPercentage/PriceDiscountPercentage';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import getConfigurationByKey from 'store/app/selector/getConfigurationByKey';
import sendClerkClickEvent from 'store/search/operation/sendClerkClickEvent';
import getCurrentPreviewMask from 'store/search/selector/getCurrentPreviewMask';
import getSearchQuery from 'store/search/selector/getSearchQuery';
import { LIST_SEARCH_RESULTS_PAGE } from 'utils/gtm/gtmSearchProductClickEvent';
import { PreviewMask, SearchResultProduct } from 'store/search/types';
import gtmSearchResultsProductClickEvent from 'utils/gtm/gtmSearchResultsProductClickEvent';
import gtmSendEventAsync from 'utils/gtm/gtmSendEventAsync';
import relativeLink from 'utils/routes/relativeLink';
import useOnScreen from 'hooks/useOnScreen';
import sendGtmProductClickOperation from 'store/category/operation/sendGtmProductClickOperation';
import gtmSendProductClickEventOperation from 'store/category/operation/gtmSendProductClickEventOperation';
import gtmSendProductImpressionOperation from 'store/category/operation/gtmSendProductImpressionOperation';
import sendGtmSearchProductClickOperation from 'store/search/operation/sendGtmSearchProductClickOperation';
import styles from './ProductItem.module.scss';

export interface OwnProps {
  product: SearchResultProduct;
  index: number;
  forceReload: boolean;
  refLink?: any;
}

const RegularPreview = ({ images }: { images: string[] }) => {
  const [main, secondary] = images;
  return (
    <>
      <img
        className={cn([styles.preview, { [styles.hasSecondaryPreview]: !!secondary }])}
        src={main}
        loading="lazy"
        alt=""
      />
      {secondary && <img className={styles.secondaryPreview} src={secondary} loading="lazy" alt="" />}
    </>
  );
};

const MaskedPreview = ({ image, mask }: { image: string; mask: PreviewMask }) => {
  return (
    <>
      <OpProductPreview
        className={cn(styles.preview, styles.hasSecondaryPreview)}
        contentSrc={image}
        backgroundSrc={mask.images[0].background}
        foregroundSrc={mask.images[0].foregroundMask}
        maskPosition={mask.imageCoordinates}
      />
      <img className={styles.secondaryPreview} src={image} loading="lazy" alt="" />
    </>
  );
};

const ProductItemContent = ({ product, refLink }: Omit<OwnProps, 'index' | 'forceReload'>) => {
  const { t } = useTranslation();
  const mask = useSelector(getCurrentPreviewMask);

  return (
    <div className={styles.productItem} ref={refLink}>
      <div className={cn(styles.favIcon, styles.showAlways)}>
        {product.sku && (
          <OpFavourite
            showFor="*"
            sku={product.sku}
            productTier={product.productTier}
            categoryId={product.categoryId}
          />
        )}
      </div>
      <div className={styles.productImageWrapper}>
        <div className={styles.productImage}>
          {mask && product.isExternalAsset ? (
            <MaskedPreview image={product.image} mask={mask} />
          ) : (
            <RegularPreview images={[product.image, product.secondaryImage]} />
          )}
        </div>
      </div>
      <div className={styles.productDescription}>
        <OpText block variant="body-medium-light" className={styles.categoryName} color="natural-70">
          {product.productTier}
        </OpText>
        <OpText block variant="body-large-regular" className={styles.productName}>
          {product.name}
        </OpText>
        <OpText block variant="label-medium-regular" className={styles.price}>
          <PriceDiscountPercentage categoryId={product.categoryId} productId={product.productId} useMinimalQuantity />
          <Price
            prefix={t('txt_from')}
            categoryId={product.categoryId}
            productId={product.productId}
            usePricePerItem
            useMinimalQuantity
          />
        </OpText>
        {product.isPersonalised && (
          <OpText className={styles.personalisedLabel} variant="body-small-regular">
            {t('txt_personalised')}
          </OpText>
        )}
      </div>
    </div>
  );
};

const ProductItem = ({ product, index, forceReload = false }: OwnProps) => {
  const ref = useRef(null);
  const [refUpdateCounter, incRefUpdateCounter] = useState(0);
  const isVisible = useOnScreen(ref, refUpdateCounter);
  const [isImpressionTracked, setIsImpressionTracked] = useState(false);

  const searchQuery = useSelector(getSearchQuery);
  const reactConfig = useSelector(getConfigurationByKey('react_app'));
  const dispatch = useDispatch();
  const productPageServedByReact =
    !forceReload && reactConfig?.useReactProductPagesForCategories?.indexOf(product.categoryId) !== -1;

  const productClick = async () => {
    dispatch(gtmSendProductClickEventOperation(product, index));
    product.encDesignId && dispatch(sendGtmSearchProductClickOperation(product.encDesignId, index));

    await Promise.all([
      gtmSendEventAsync(gtmSearchResultsProductClickEvent(product.productTier, product.name, searchQuery, product.sku)),
      sendClerkClickEvent(product.id),
    ]);
  };

  const refWrapper = useCallback((node) => {
    incRefUpdateCounter((v) => v + 1);
    ref.current = node;
  }, []);

  useEffect(() => {
    if (isVisible && !isImpressionTracked) {
      dispatch(gtmSendProductImpressionOperation(product, index));
      setIsImpressionTracked(true);
    }
  }, [isVisible, isImpressionTracked]);

  return productPageServedByReact ? (
    <Link
      to={relativeLink(product.url)}
      className={cn(styles.productContainer, 'product')}
      onClick={productClick}
      data-gtm-position={index}
      data-gtm-enc-design-id={product.productId}
    >
      <ProductItemContent product={product} refLink={refWrapper} />
    </Link>
  ) : (
    <a
      href={product.url}
      className={cn(styles.productContainer, 'product')}
      onClick={productClick}
      data-gtm-position={index}
      data-gtm-enc-design-id={product.productId}
    >
      <ProductItemContent product={product} refLink={refWrapper} />
    </a>
  );
};

export default ProductItem;
